import { SessionStorageKeys } from '@aglenergy/shared-library';
import { ObservableMap } from '@stencil/store';
import { State } from 'app/shared/store/models/state.model';
import { nbnProductService } from 'app/nbn/services/nbn-product/nbn-product.service';
import { removeHandler } from 'app/shared/store/handlers/types/remove-handler';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';
import { AddressDetailModel } from 'app/shared/models/address/address.model';
import { crossSellService } from 'app/shared/services/cross-sell/cross-sell.service';
import { delayMessageService } from 'app/shared/services/delay-message/delay-message.service';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { altElementSelector, scrollToElement } from 'utils/dom.helper';
import { bundleService } from 'app/shared/services/bundle/bundle.service';
import { spinnerService } from 'app/shared/services/spinner/spinner.service';

export const addAddressHandler = (store: ObservableMap<State>): removeHandler => {
  return store.onChange('address', async (address) => {
    await searchNbnProductAddress(store, address);
  });
};

export const searchNbnProductAddress = async (store: ObservableMap<State>, address: AddressDetailModel): Promise<void> => {
  store.set('isPersonalisedProductsLoading', true);
  store.set(StoreKeys.responseError, null);
  delayMessageService.startAllTimers();
  spinnerService.showPlansSkeletonLoader();

  if (!bundleService.isExplicitBundleFlow()) {
    crossSellService.loadCrossSellProducts(address);
  }

  await nbnProductService
    .getProducts(address)
    .then((productDetails) => {
      const products = productDetails?.products ?? null;
      //Send data to analytics
      const techTypes = products ? products.map((x) => x.attributes.tech_type).join() : '';
      const eventData = techTypes ? Array.from(new Set(techTypes.split(','))).toString() : '';
      const data = {
        entered_state: address?.state,
        entered_suburb: address?.suburb,
        entered_post_code: address?.postcode,
        event_additional_data: `Fibre connect upgrade ${
          productDetails.serviceQualification?.nbn.alternativeServiceType ? 'eligible' : 'not eligible'
        }`
      };
      tealiumAnalyticService.createAndSendAddressData(['Address Validated'], eventData, data, 'nbn');
      tealiumAnalyticService.trackProductCards(products, false, 'nbn');
      store.set(StoreKeys.productDetails, productDetails);
      store.set(StoreKeys.responseError, null);

      if (!store.get(StoreKeys.aemCIFIntegrationEnabled)) {
        // scroll to cards or great news
        altElementSelector('message-wrapper#sq-message', 'app-product-cards').then(
          (el) => scrollToElement(el, 0),
          (r) => console.debug(r)
        );
      }
    })
    .catch((error) => {
      store.set(StoreKeys.responseError, error);
      if (store.get(StoreKeys.aemCIFIntegrationEnabled)) {
        window.postMessage(
          {
            eventType: 'nbn-error',
            spaCategory: store.get('productCategory'),
            eventData: {
              errorType: error.errorType
            }
          },
          '*'
        );
      }
    })
    .finally(() => {
      store.set('isPageLoaded', true);
      store.set('isPersonalisedProductsLoading', false);
      delayMessageService.stopAllTimers();
      spinnerService.removePlansSkeletonLoader();
    });

  // if bundleSearchAddress exists, set it as default address
  const bundleSearchAddressJson = sessionStorage.getItem(SessionStorageKeys.BundleSearchAddress);
  if (bundleSearchAddressJson) {
    const bundleSearchAddress = JSON.parse(bundleSearchAddressJson);
    store.set('address', bundleSearchAddress);
    sessionStorage.removeItem(SessionStorageKeys.BundleSearchAddress);
  }
};
